const TRANSITION_DURATION = 500;
const TRANSITION_EASING = 'ease-out';
const RESTING_TRANSITION = 'all 0s ease 0s';
const AUTOPLAY_TIMER_PADDING = 50;
const SLIDE_HEIGHT = 40;
const TOUCH_THRESHOLD = 5;
const VERTICAL_SCROLL_THRESHOLD = 10;
const HORIZONTAL_DOMINANCE_RATIO = 0.8;
const RESIZE_DEBOUNCE = 50;
const POST_TRANSITION_LOCK = 10;

class AnnouncementBar {
  #root;
  #track;
  #list;
  #pauseButton;
  #slides = [];
  #current = 0;
  #trackIndex = 1;
  #autoplaying = null;
  #autoplayTimer;
  #animationTimer;
  #animationUnlockTimer;
  #resizeTimer;
  #callbackTimers = new Set();
  #resizeObserver;
  #isAnimating = false;
  #clickable = true;
  #dragging = false;
  #scrolling = false;
  #swiping = false;
  #swiped = false;
  #touchObject = {};
  #listWidth = 0;

  constructor(root) {
    this.#root = root;
    this.#track = root.querySelector('.announcement-bar__track');
    this.#list = root.querySelector('.announcement-bar__slides');
    this.#pauseButton = root.closest('.announcement-bar')?.querySelector('[data-announcement-pause]');
  }

  connect() {
    if (!this.#track || !this.#list) return;

    this.#prepareTrack();
    if (this.#slides.length < 2) return;

    this.#pauseButton?.addEventListener('click', this.#togglePlayback);
    this.#list.addEventListener('click', this.#handleTrackClick);
    this.#list.addEventListener('mousedown', this.#handleMouseDown);
    this.#list.addEventListener('mouseup', this.#handleMouseUp);
    this.#list.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    this.#list.addEventListener('touchend', this.#handleTouchEnd);
    this.#list.addEventListener('keydown', this.#handleKeyDown);
    window.addEventListener('resize', this.#handleWindowResize);

    this.#autoPlay('update');

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleObservedResize);
      this.#resizeObserver.observe(this.#list);
    }
  }

  destroy() {
    this.#clearAutoplayTimer();
    this.#clearAnimationTimers();
    clearTimeout(this.#resizeTimer);
    this.#callbackTimers.forEach((timer) => clearTimeout(timer));
    this.#callbackTimers.clear();
    this.#resizeObserver?.disconnect();
    this.#pauseButton?.removeEventListener('click', this.#togglePlayback);
    this.#list?.removeEventListener('click', this.#handleTrackClick);
    this.#list?.removeEventListener('mousedown', this.#handleMouseDown);
    this.#list?.removeEventListener('mousemove', this.#handleMouseMove);
    this.#list?.removeEventListener('mouseup', this.#handleMouseUp);
    this.#list?.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.#list?.removeEventListener('touchstart', this.#handleTouchStart);
    this.#list?.removeEventListener('touchmove', this.#handleTouchMove);
    this.#list?.removeEventListener('touchend', this.#handleTouchEnd);
    this.#list?.removeEventListener('touchcancel', this.#handleTouchCancel);
    this.#list?.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#handleWindowResize);
  }

  #prepareTrack() {
    this.#slides = Array.from(this.#track.children).filter((slide) => !slide.classList.contains('slick-cloned'));

    if (this.#track.children.length !== this.#slides.length * 2 + 1) {
      this.#createInfiniteTrack();
    }

    const activeOriginal = Array.from(this.#track.children).findIndex(
      (slide) => !slide.classList.contains('slick-cloned') && slide.dataset.index === '0'
    );

    this.#trackIndex = activeOriginal >= 0 ? activeOriginal : 1;
    this.#current = 0;
    this.#measureSlides();
    this.#track.style.width = '';
    this.#track.style.left = '';
    this.#list.style.height = `${SLIDE_HEIGHT}px`;
    this.#track.style.height = `${(this.#slides.length + 2) * SLIDE_HEIGHT}px`;
    this.#track.style.opacity = '1';
    this.#setRestingTransition();
    this.#setTrackPosition(this.#trackIndex);
    this.#updateAccessibility();
  }

  #createInfiniteTrack() {
    this.#track.querySelectorAll('.slick-cloned').forEach((slide) => slide.remove());
    this.#slides = Array.from(this.#track.children);
    if (!this.#slides.length) return;

    const lastClone = this.#slides.at(-1).cloneNode(true);
    this.#sanitizeClone(lastClone);
    lastClone.dataset.index = '-1';
    lastClone.classList.add('slick-cloned');
    this.#track.prepend(lastClone);

    this.#slides.forEach((slide, index) => {
      slide.dataset.index = `${index}`;
      const clone = slide.cloneNode(true);
      this.#sanitizeClone(clone);
      clone.dataset.index = `${this.#slides.length + index}`;
      clone.classList.add('slick-cloned');
      this.#track.append(clone);
    });
  }

  #sanitizeClone(clone) {
    for (const element of [clone, ...clone.querySelectorAll('*')]) {
      element.removeAttribute('id');
      element.removeAttribute('ref');
      element.removeAttribute('data-shopify-editor-block');
      element.removeAttribute('data-block-id');
    }

    clone.classList.remove('slick-active', 'slick-current');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
  }

  #slideHandler(direction) {
    if (this.#isAnimating) return false;

    this.#isAnimating = true;
    const nextIndex = this.#current + direction;
    this.#current = (nextIndex + this.#slides.length) % this.#slides.length;
    this.#trackIndex = nextIndex + 1;
    this.#updateAccessibility();
    this.#track.style.transition = `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`;
    this.#setTrackPosition(this.#trackIndex);

    this.#clearAnimationTimers();
    this.#animationTimer = setTimeout(this.#completeVisualTransition, TRANSITION_DURATION);
    return true;
  }

  #completeVisualTransition = () => {
    this.#animationTimer = undefined;
    this.#setRestingTransition();

    if (this.#trackIndex > this.#slides.length) {
      this.#trackIndex = 1;
      this.#setTrackPosition(this.#trackIndex);
    } else if (this.#trackIndex < 1) {
      this.#trackIndex = this.#slides.length;
      this.#setTrackPosition(this.#trackIndex);
    }

    this.#updateAccessibility();
    this.#animationUnlockTimer = setTimeout(() => {
      this.#animationUnlockTimer = undefined;
      this.#isAnimating = false;
    }, POST_TRANSITION_LOCK);
  };

  #changeSlide(direction) {
    this.#slideHandler(direction);
    if (this.autoplay) this.#autoPlay('update');
  }

  #playNext = () => this.#slideHandler(1);

  #autoPlay(mode) {
    this.#clearAutoplayTimer();

    if (mode === 'update' && ['hovered', 'focused', 'paused'].includes(this.#autoplaying)) return;
    if (mode === 'leave' && ['paused', 'focused'].includes(this.#autoplaying)) return;
    if (mode === 'blur' && ['paused', 'hovered'].includes(this.#autoplaying)) return;

    this.#autoplayTimer = setInterval(this.#playNext, this.autoplayInterval + AUTOPLAY_TIMER_PADDING);
    this.#autoplaying = 'playing';
  }

  #pause(mode) {
    this.#clearAutoplayTimer();

    if (mode === 'paused') {
      this.#autoplaying = 'paused';
    } else if (mode === 'focused' && ['hovered', 'playing'].includes(this.#autoplaying)) {
      this.#autoplaying = 'focused';
    } else if (this.#autoplaying === 'playing') {
      this.#autoplaying = 'hovered';
    }
  }

  #clearAutoplayTimer() {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  #clearAnimationTimers() {
    clearTimeout(this.#animationTimer);
    clearTimeout(this.#animationUnlockTimer);
    this.#animationTimer = undefined;
    this.#animationUnlockTimer = undefined;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = parseInt(`${this.#root.dataset.autoplay}`, 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get paused() {
    return this.#autoplaying === 'paused';
  }

  #togglePlayback = () => {
    if (this.paused) {
      this.#autoPlay('play');
    } else {
      this.#pause('paused');
    }

    this.#updatePauseButton();
  };

  #updatePauseButton() {
    if (!this.#pauseButton) return;

    const label = this.paused ? 'Play slide rotation' : 'Pause slide rotation';
    const icon = this.#pauseButton.querySelector('i');
    this.#pauseButton.querySelector('.sr-only').textContent = label;
    icon?.classList.toggle('icon-pause-filled', !this.paused);
    icon?.classList.toggle('icon-play-filled', this.paused);
  }

  #setRestingTransition() {
    this.#track.style.transition = RESTING_TRANSITION;
  }

  #setTrackPosition(index) {
    this.#track.style.transform = `translate3d(0, ${-SLIDE_HEIGHT * index}px, 0)`;
  }

  #updateAccessibility() {
    Array.from(this.#track.children).forEach((slide) => {
      const isActive = !slide.classList.contains('slick-cloned') && Number(slide.dataset.index) === this.#current;
      slide.setAttribute('tabindex', '-1');
      slide.setAttribute('aria-hidden', `${!isActive}`);
      slide.classList.toggle('slick-active', isActive);
      slide.classList.toggle('slick-current', isActive);
    });
  }

  #scheduleResize(updateTrack = true) {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#resizeWindow(updateTrack), RESIZE_DEBOUNCE);
  }

  #resizeWindow(updateTrack = true) {
    if (!this.#track || !this.#list) return;

    this.#measureSlides();
    this.#list.style.height = `${SLIDE_HEIGHT}px`;
    this.#track.style.height = `${(this.#slides.length + 2) * SLIDE_HEIGHT}px`;

    if (updateTrack) {
      this.#trackIndex = this.#current + 1;
      this.#setRestingTransition();
      this.#setTrackPosition(this.#trackIndex);
      this.#updateAccessibility();
    }

    if (this.autoplay) this.#autoPlay('update');
    this.#isAnimating = false;
    this.#clearAnimationTimers();
  }

  #handleObservedResize = () => {
    if (this.#isAnimating) {
      this.#scheduleResize(false);
      const timer = setTimeout(() => {
        this.#callbackTimers.delete(timer);
        this.#scheduleResize(true);
      }, TRANSITION_DURATION);
      this.#callbackTimers.add(timer);
    } else {
      this.#scheduleResize(true);
    }
  };

  #handleWindowResize = () => this.#scheduleResize(true);

  #measureSlides() {
    this.#listWidth = Math.ceil(this.#list.offsetWidth);
    Array.from(this.#track.children).forEach((slide) => {
      slide.style.width = `${this.#listWidth}px`;
    });
  }

  #preventEvent(event) {
    if (event?.cancelable && !['touchstart', 'touchmove', 'wheel'].includes(event.type)) {
      event.preventDefault();
    }
  }

  #eventPoint(event) {
    const touch = event.touches?.[0];
    return {
      x: touch ? touch.pageX : event.clientX,
      y: touch ? touch.pageY : event.clientY,
    };
  }

  #swipeStart(event) {
    if (event.target.tagName === 'IMG') this.#preventEvent(event);

    const point = this.#eventPoint(event);
    this.#dragging = true;
    this.#touchObject = {
      startX: point.x,
      startY: point.y,
      curX: point.x,
      curY: point.y,
    };
  }

  #swipeMove(event) {
    if (this.#scrolling || !this.#dragging) return;
    if (this.#isAnimating) {
      this.#preventEvent(event);
      return;
    }

    const point = this.#eventPoint(event);
    this.#touchObject.curX = point.x;
    this.#touchObject.curY = point.y;

    const deltaX = point.x - this.#touchObject.startX;
    const deltaY = point.y - this.#touchObject.startY;
    const swipeLength = Math.round(Math.sqrt(deltaX ** 2));
    const verticalLength = Math.round(Math.sqrt(deltaY ** 2));
    this.#touchObject.swipeLength = swipeLength;

    if (!this.#swiping && verticalLength > VERTICAL_SCROLL_THRESHOLD) {
      this.#scrolling = true;
      return;
    }

    const direction = point.x > this.#touchObject.startX ? 1 : -1;
    const swipePosition = -SLIDE_HEIGHT * (this.#current + 1) + (SLIDE_HEIGHT / this.#listWidth) * swipeLength * direction;

    this.#setRestingTransition();
    this.#track.style.transform = `translate3d(0, ${swipePosition}px, 0)`;

    if (Math.abs(deltaX) >= HORIZONTAL_DOMINANCE_RATIO * Math.abs(deltaY) && swipeLength > 10) {
      this.#swiping = true;
      this.#clickable = false;
      this.#preventEvent(event);
    }
  }

  #swipeDirection() {
    const horizontalDistance = this.#touchObject.startX - this.#touchObject.curX;
    let angle = Math.round(
      (Math.atan2(this.#touchObject.startY - this.#touchObject.curY, horizontalDistance) * 180) / Math.PI
    );

    if (angle < 0) angle = 360 - Math.abs(angle);
    if ((angle >= 0 && angle <= 45) || (angle >= 315 && angle <= 360)) return 'left';
    if (angle >= 135 && angle <= 225) return 'right';
    return 'vertical';
  }

  #swipeEnd(event) {
    if (!this.#dragging) {
      this.#preventEvent(event);
      return;
    }

    const swipeLength = this.#touchObject.swipeLength;
    const scrolling = this.#scrolling;
    const direction = this.#swipeDirection();

    this.#dragging = false;
    this.#scrolling = false;
    this.#swiping = false;
    this.#swiped = false;
    this.#touchObject = {};
    this.#removeDragListeners();

    if (scrolling || !swipeLength) return;

    const threshold = this.#listWidth / TOUCH_THRESHOLD;
    if (swipeLength > threshold) {
      this.#preventEvent(event);
      this.#slideHandler(direction === 'left' ? 1 : direction === 'right' ? -1 : 0);
      return;
    }

    this.#track.style.transition = `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`;
    this.#setTrackPosition(this.#current + 1);
  }

  #addDragListeners() {
    this.#list.addEventListener('mousemove', this.#handleMouseMove);
    this.#list.addEventListener('mouseleave', this.#handleMouseLeave);
    this.#list.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.#list.addEventListener('touchcancel', this.#handleTouchCancel);
  }

  #removeDragListeners() {
    this.#list.removeEventListener('mousemove', this.#handleMouseMove);
    this.#list.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.#list.removeEventListener('touchmove', this.#handleTouchMove);
    this.#list.removeEventListener('touchcancel', this.#handleTouchCancel);
  }

  #handleMouseDown = (event) => {
    this.#swipeStart(event);
    this.#addDragListeners();
  };

  #handleMouseMove = (event) => this.#swipeMove(event);
  #handleMouseUp = (event) => this.#swipeEnd(event);
  #handleMouseLeave = (event) => this.#swipeEnd(event);

  #handleTouchStart = (event) => {
    this.#swipeStart(event);
    this.#addDragListeners();
  };

  #handleTouchMove = (event) => this.#swipeMove(event);

  #handleTouchEnd = (event) => {
    this.#swipeEnd(event);
    this.#clickable = true;
  };

  #handleTouchCancel = (event) => this.#swipeEnd(event);

  #handleTrackClick = (event) => {
    if (!this.#clickable) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.#clickable = true;
  };

  #handleKeyDown = (event) => {
    if (event.target.tagName.match('TEXTAREA|INPUT|SELECT')) return;

    if (event.keyCode === 37) this.#changeSlide(-1);
    if (event.keyCode === 39) this.#changeSlide(1);
  };
}

const instances = new WeakMap();

function initializeAnnouncementBars(scope = document) {
  const roots = [];
  if (scope.matches?.('[data-announcement-slider]')) roots.push(scope);
  roots.push(...(scope.querySelectorAll?.('[data-announcement-slider]') ?? []));

  roots.forEach((root) => {
    if (instances.has(root)) return;
    const instance = new AnnouncementBar(root);
    instances.set(root, instance);
    instance.connect();
  });
}

function destroyAnnouncementBars(scope) {
  const roots = [];
  if (scope.matches?.('[data-announcement-slider]')) roots.push(scope);
  roots.push(...(scope.querySelectorAll?.('[data-announcement-slider]') ?? []));

  roots.forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  });
}

initializeAnnouncementBars();
document.addEventListener('shopify:section:load', (event) => initializeAnnouncementBars(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyAnnouncementBars(event.target));
